export interface AppSettingsConfig {
  EnableMultiFileDownload: boolean;
  MaxDownloadFiles: number;
  EnableNotifications: boolean;
  MAX_FILES_PER_UPLOAD: number;
  MAX_FILE_SIZE_BYTES: number;
  ALLOWED_EXTENSIONS: string[];
}

// Shape of the "appSettings" backend config (api/config/appSettings, config_value JSON).
// Flat and global — every field is a straight override, no per-client/per-project scoping
// (unlike kpiConfig's pm.default/pm.projects — nothing here has ever varied per deployment).
export interface RemoteAppConfig {
  EnableMultiFileDownload?: boolean;
  MaxDownloadFiles?: number;
  EnableNotifications?: boolean;
  MAX_FILES_PER_UPLOAD?: number;
  MAX_FILE_SIZE_BYTES?: number;
  ALLOWED_EXTENSIONS?: string[];
}

const isDevRuntime = typeof __DEV__ === 'undefined' || __DEV__;

// Diagnostic only — prints a source breakdown so it's obvious at a glance which fields came
// from the backend config vs the hardcoded fallback in this file. Safe to remove once the
// backend-driven config is trusted; has no effect on the resolved values themselves.
function logAppConfigProvenance(remote: RemoteAppConfig): void {
  if (!isDevRuntime) return;

  const rows: Record<string, string> = {
    EnableMultiFileDownload: remote.EnableMultiFileDownload !== undefined ? 'backend' : 'hardcoded',
    MaxDownloadFiles:        remote.MaxDownloadFiles !== undefined ? 'backend' : 'hardcoded',
    EnableNotifications:     remote.EnableNotifications !== undefined ? 'backend' : 'hardcoded',
    MAX_FILES_PER_UPLOAD:    remote.MAX_FILES_PER_UPLOAD !== undefined ? 'backend' : 'hardcoded',
    MAX_FILE_SIZE_BYTES:     remote.MAX_FILE_SIZE_BYTES !== undefined ? 'backend' : 'hardcoded',
    ALLOWED_EXTENSIONS:      remote.ALLOWED_EXTENSIONS ? `backend (${remote.ALLOWED_EXTENSIONS.length} extensions)` : 'hardcoded',
  };

  // eslint-disable-next-line no-console
  console.groupCollapsed('[appConfig] source breakdown');
  // eslint-disable-next-line no-console
  console.table(rows);
  // eslint-disable-next-line no-console
  console.groupEnd();
}

// Resolves hardcoded DEFAULT_APP_CONFIG <- remote, field by field. Use `??` (not `||`) so an
// explicit backend `false`/`0`/`[]` is honored rather than silently falling back to the default.
export function resolveAppConfig(remote: RemoteAppConfig | null | undefined): AppSettingsConfig {
  if (!remote) {
    if (isDevRuntime) {
      // eslint-disable-next-line no-console
      console.warn('[appConfig] backend config unavailable — rendering 100% hardcoded DEFAULT_APP_CONFIG');
    }
    return DEFAULT_APP_CONFIG;
  }

  logAppConfigProvenance(remote);

  return {
    EnableMultiFileDownload: remote.EnableMultiFileDownload ?? DEFAULT_APP_CONFIG.EnableMultiFileDownload,
    MaxDownloadFiles:        remote.MaxDownloadFiles ?? DEFAULT_APP_CONFIG.MaxDownloadFiles,
    EnableNotifications:     remote.EnableNotifications ?? DEFAULT_APP_CONFIG.EnableNotifications,
    MAX_FILES_PER_UPLOAD:    remote.MAX_FILES_PER_UPLOAD ?? DEFAULT_APP_CONFIG.MAX_FILES_PER_UPLOAD,
    MAX_FILE_SIZE_BYTES:     remote.MAX_FILE_SIZE_BYTES ?? DEFAULT_APP_CONFIG.MAX_FILE_SIZE_BYTES,
    ALLOWED_EXTENSIONS:      remote.ALLOWED_EXTENSIONS ?? DEFAULT_APP_CONFIG.ALLOWED_EXTENSIONS,
  };
}

// Default values — code-level fallback used when the backend config is unavailable. Matches
// today's prod services/Constants.ts values verbatim.
export const DEFAULT_APP_CONFIG: AppSettingsConfig = {
  EnableMultiFileDownload: false,
  MaxDownloadFiles: 10,
  EnableNotifications: false,
  MAX_FILES_PER_UPLOAD: 10,
  MAX_FILE_SIZE_BYTES: 262144000, // 250MB
  ALLOWED_EXTENSIONS: ['pdf', 'jpe', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'ppt', 'pptx', 'kmz'],
};
